import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, qs } from '../lib/api';
import { date, eur } from '../lib/format';
import { useSession } from '../lib/session';
import { Badge, Empty, ErrorBox, Loading, PageHead, StatusBadge, Tabs, useAction, useLoad } from '../components/ui';

const VIEWS = [
  { id: 'todo', label: 'Por revisar', status: 'review,received,processing' },
  { id: 'pending_approval', label: 'En aprobación', status: 'pending_approval' },
  { id: 'approved', label: 'Aprobadas', status: 'approved' },
  { id: 'done', label: 'Contabilizadas y pagadas', status: 'exported,scheduled,paid' },
  { id: 'rejected', label: 'Rechazadas y anuladas', status: 'rejected,cancelled' },
  { id: 'all', label: 'Todas', status: '' },
] as const;

export function Inbox({ mine = false }: { mine?: boolean }) {
  const s = useSession();
  const nav = useNavigate();
  const { busy, run } = useAction();
  const [view, setView] = useState<(typeof VIEWS)[number]['id']>(mine ? 'pending_approval' : 'todo');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [sel, setSel] = useState<Set<string>>(new Set());
  const status = VIEWS.find((v) => v.id === view)!.status;
  const { data, error, loading, reload, refresh } = useLoad(
    () => api.get(`/invoices${qs({ companyId: s.companyId, status: mine ? 'pending_approval' : status, q, page, assignedToMe: mine || undefined, sort: view === 'approved' ? 'due' : 'received' })}`),
    [s.companyId, status, q, page, mine],
  );
  // La lista se pone al día sola cada 20 s (con la pestaña a la vista), para que una factura que llega por correo aparezca sin pulsar «Actualizar»
  useEffect(() => {
    const t = setInterval(() => { if (!document.hidden) refresh(); }, 20_000);
    return () => clearInterval(t);
  }, []);
  const counts = data?.counts ?? {};
  const countFor = (st: string) => st.split(',').filter(Boolean).reduce((a, x) => a + (counts[x] ?? 0), 0);
  const toggle = (id: string) => setSel((x) => { const n = new Set(x); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const items: any[] = data?.items ?? [];
  const bulk = async (action: 'approve' | 'submit') => {
    const r = await run(() => api.post('/invoices/bulk', { ids: [...sel], action }));
    if (r) {
      const ok = r.results.filter((x: any) => x.ok).length;
      const failed = r.results.filter((x: any) => !x.ok);
      alert(`${ok} correcta(s).${failed.length ? `\n\nNo se pudo con ${failed.length}:\n${failed.map((f: any) => `· ${f.error}`).join('\n')}` : ''}`);
      setSel(new Set());
      reload();
    }
  };

  return (
    <>
      <PageHead
        title={mine ? 'Mis aprobaciones' : 'Bandeja de facturas'}
        subtitle={mine ? 'Facturas que esperan tu decisión.' : s.company ? <>Reenvía facturas a <code>{s.company.inboxAddress}</code> o arrástralas a esta ventana.</> : 'Todas las empresas.'}
        actions={sel.size > 0 && <>
          <span className="muted">{sel.size} seleccionada(s)</span>
          {(view === 'todo') && s.can('invoice.submit') && <button disabled={busy} onClick={() => bulk('submit')}>Enviar a aprobación</button>}
          {(view === 'pending_approval' || mine) && s.can('invoice.approve') && <button className="ok" disabled={busy} onClick={() => bulk('approve')}>Aprobar</button>}
        </>}
      />
      {!mine && <Tabs value={view} onChange={(v) => { setView(v); setPage(1); setSel(new Set()); }} items={VIEWS.map((v) => ({ id: v.id, label: <>{v.label}{v.status && countFor(v.status) ? <Badge>{countFor(v.status)}</Badge> : null}</> }))} />}
      <div className="row" style={{ marginBottom: 12 }}>
        <input placeholder="Buscar por proveedor, número, NIF o importe…" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} style={{ maxWidth: 420 }} />
        <button className="ghost" onClick={reload}>Actualizar</button>
      </div>
      <ErrorBox error={error} />
      {loading && !data ? <Loading /> : items.length === 0 ? (
        <Empty>{mine ? 'No tienes facturas pendientes de aprobar. 🎉' : 'No hay facturas en esta vista.'}</Empty>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th className="check"><input type="checkbox" aria-label="Seleccionar todas" checked={sel.size === items.length && items.length > 0} onChange={(e) => setSel(e.target.checked ? new Set(items.map((i) => i.id)) : new Set())} /></th>
                <th>Proveedor</th><th>Número</th><th>Fecha</th><th>Vencimiento</th><th className="num">Total</th><th>Estado</th><th>Incidencias</th><th>Origen</th>
              </tr>
            </thead>
            <tbody>
              {items.map((i) => (
                <tr key={i.id} className="clickable" onClick={() => nav(`/facturas/${i.id}`)}>
                  <td className="check" onClick={(e) => e.stopPropagation()}><input type="checkbox" aria-label="Seleccionar" checked={sel.has(i.id)} onChange={() => toggle(i.id)} /></td>
                  <td><strong>{i.vendor_name ?? <span className="muted">{i.filename ?? 'Leyendo…'}</span>}</strong><div className="muted mono">{i.vendor_nif}</div></td>
                  <td className="mono">{i.number ?? '—'}</td>
                  <td>{date(i.issue_date)}</td>
                  <td>{date(i.due_date)}</td>
                  <td className="num">{eur(i.total)}</td>
                  <td><StatusBadge status={i.status} />{i.touchless && <> <Badge tone="ok">Automática</Badge></>}</td>
                  <td>
                    {i.errors > 0 && <Badge tone="error">{i.errors} error{i.errors > 1 ? 'es' : ''}</Badge>}{' '}
                    {i.warnings > 0 && <Badge tone="warn">{i.warnings} aviso{i.warnings > 1 ? 's' : ''}</Badge>}
                    {i.status === 'review' && i.review_reasons?.[0] && <div className="hint" style={{ maxWidth: 320 }}>{i.review_reasons[0]}</div>}
                  </td>
                  <td>
                    <Badge tone={i.source === 'xml' ? 'ok' : i.source === 'ai' ? 'info' : ''}>{i.einvoice_format ? 'Factura electrónica' : i.source === 'ai' ? 'IA' : i.source === 'heuristic' ? 'Texto' : i.source === 'manual' ? 'Manual' : '—'}</Badge>
                    <div className="hint">{i.channel === 'email' ? 'Correo' : i.channel === 'mobile' ? 'Móvil' : i.channel === 'api' ? 'API' : 'Subida'}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data && data.total > data.pageSize && (
        <div className="row" style={{ marginTop: 12 }}>
          <button disabled={page <= 1} onClick={() => setPage(page - 1)}>Anterior</button>
          <span className="muted">Página {page} de {Math.ceil(data.total / data.pageSize)}</span>
          <button disabled={page * data.pageSize >= data.total} onClick={() => setPage(page + 1)}>Siguiente</button>
        </div>
      )}
    </>
  );
}
